<template>
  <v-card
    elevation="0"
    class="v-card"
  >
    <v-overlay
      v-model="showLoader"
      class="d-flex align-center justify-center"
      opacity="0.2"
      persistent
    >
      <v-progress-circular
        indeterminate
        size="60"
        color="primary"
      ></v-progress-circular>
    </v-overlay>
    <v-card-title
      class="title"
    >
      Podaci o lokaciji
    </v-card-title>

    <v-card-text
      class="v-card-text"
    >
      <v-row>
        <v-col>
          <v-row>
            <v-col
              @dblclick="$emit('set-edit', true)"
            >
              <autocomplete-field
                v-model="formDataCopy[cityDistrictField.key]"
                :field="cityDistrictField"
                :options="cityDistrictField.autocomplete_options"
                :validation-errors="validationErrors"
                :disabled="!edit && !createMode"
                @clear-validation-errors="$emit('clear-validation-errors', cityDistrictField.key)"
                @items="items => cityDistricts = items"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col
              @dblclick="$emit('set-edit', true)"
            >
              <autocomplete-field
                v-model="formDataCopy[addressField.key]"
                :field="addressField"
                :items="streetsItems"
                :options="addressField.autocomplete_options"
                :disabled="!edit && !createMode"
                :validation-errors="validationErrors"
                @clear-validation-errors="$emit('clear-validation-errors', addressField.key)"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col
              @dblclick="$emit('set-edit', true)"
            >
              <text-field
                v-model="formDataCopy[houseNumberField.key]"
                :field="houseNumberField"
                :disabled="!edit && !createMode"
                :validation-errors="validationErrors"
                @clear-validation-errors="$emit('clear-validation-errors', houseNumberField.key)"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col>
          <google-map
            :config="mapConfig"
            external-map-type-set="here-classic"
            @map:objects="onMapObjects"
          />
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import GoogleMap from '@/global/components/google-map/components/GoogleMap.vue'
import TextField from '@/global/components/view-layouts/tabs-layout/components/fields/TextField.vue'
import AutocompleteField from '@/global/components/view-layouts/tabs-layout/components/fields/AutocompleteField.vue'
import { debounce, isArray, isObject } from 'lodash'
import { api } from '@/global/services/api'

const GEOREVERSE_TIMEOUT_IN_MS = 500
const DEFAULT_ZOOM_LEVEL = 19
export default {
  name: 'LocationInformation',

  components: {
    AutocompleteField,
    TextField,
    GoogleMap
  },

  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    edit: {
      type: Boolean,
      default: false
    },
    createMode: {
      type: Boolean,
      default: false
    },
    updateData: {
      type: Function,
      default: () => {}
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      cityDistrictField: {
        key: 'city_district',
        label: this.$t('road-maintenance/aggregator.city_district') + '*',
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'traffic-regulation/city-districts',
          allowSearch: false,
          returnValue: 'object'
        },
        onSelect: async (event) => await this.onCityDistrictSelection(event),
        onInput: (event) => this.onCityDistrictInput(event),
        creatable: true,
        editable: true,
        visible: true
      },
      addressField: {
        key: 'address_street',
        label: this.$t('road-maintenance/aggregator.address_street') + '*',
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'traffic-regulation/roads/autocomplete',
          returnValue: 'object',
          fetchInitial: false,
          additionalQueryParams: {}
        },
        onInput: (event) => this.onStreetInput(event),
        onSelect: (event) => this.onStreetSelection(event),
        creatable: true,
        editable: true,
        visible: true
      },
      houseNumberField: {
        key: 'address_number',
        label: this.$t('road-maintenance/aggregator.house_number') + '*',
        onInput: (value, _) => this.onHouseNumberInput(value),
        creatable: true,
        editable: true,
        visible: true
      },
      map: null,
      googleMaps: null,
      polygon: null,
      marker: null,
      street: '',
      houseNumber: '',
      AdvancedMarker: null,
      showLoader: false,
      formDataCopy: {},
      manuallyChangingLocation: false,
      streetsItems: {
        address_street: {
          data: []
        }
      },
      lastSelectedCityDistrict: null,
      lastSelectedStreet: null,
      cityDistricts: {},
      handlersAfterMapInitialization: [],
      mapConfig: {
        disablePoiClicks: true,
        style: {
          borderRadius: '16px'
        }
      },
      mapClickListener: null
    }
  },

  watch: {
    formData: {
      immediate: true,
      deep: true,
      handler (val) {
        if (val && isObject(val)) {
          this.formDataCopy = val
        }
      }
    },
    edit: {
      immediate: true,
      async handler (val) {
        // Sets marker draggable value based on edit mode value
        // Marker is draggable only if edit is enabled
        if (this.marker) {
          this.marker.gmpDraggable = !!val
        }
        // Reset marker position when edit is disabled
        if (!val && this.formDataCopy.latitude && this.formDataCopy.longitude && this.marker) {
          if (this.map && this.googleMaps) {
            this.marker.position = { lat: this.formDataCopy.latitude, lng: this.formDataCopy.longitude }
            const bounds = new this.googleMaps.LatLngBounds()
            bounds.extend(this.marker.position)

            this.map.fitBounds(bounds)
            this.map.setZoom(DEFAULT_ZOOM_LEVEL)
          }
          else {
            this.handlersAfterMapInitialization.push(() => {
              this.marker.position = { lat: this.formDataCopy.latitude, lng: this.formDataCopy.longitude }
              const bounds = new this.googleMaps.LatLngBounds()
              bounds.extend(this.marker.position)

              this.map.fitBounds(bounds)
              this.map.setZoom(DEFAULT_ZOOM_LEVEL)
            })
          }
        }
        if (!val || this.createMode) {
          await this.fetchAddressStreets()
        }
      }
    }
  },

  methods: {
    async onMapObjects (event) {
      const { map, googleMaps, advancedMarker } = event || {}

      this.map = map ?? null
      this.googleMaps = googleMaps ?? null

      if (this.map && this.googleMaps) {
        this.AdvancedMarker = advancedMarker

        // set map click and dragend events
        this.setMapClickAndDragendEvents()
        if (this.handlersAfterMapInitialization.length) {
          for (const handler of this.handlersAfterMapInitialization) {
            if (typeof handler === 'function') {
              await handler()
            }
          }
          this.handlersAfterMapInitialization = []
        }
      }
    },

    async fetchAddressStreets (usePickedId = true) {
      const { data } = await api('road-maintenance').get('traffic-regulation/roads/autocomplete', {
        city_district_id: this.formDataCopy?.city_district?.id ?? this.formDataCopy?.city_district ?? null,
        picked_id: usePickedId ? (this.formDataCopy?.address_street?.id ?? this.formDataCopy?.address_street ?? null) : null
      })

      if (isArray(data)) {
        this.$set(this.streetsItems.address_street, 'data', data)
      }
    },

    async onCityDistrictInput (event) {
      const { value } = event || {}
      if (this.formDataCopy?.city_district?.data && this.formDataCopy.city_district.data.includes('POLYGON') && value) {
        const polygon = this.formDataCopy.city_district.data.replace('POLYGON ((', '').replace('))', '')
        this.lastSelectedCityDistrict = this.formDataCopy.city_district
        this.$set(this.addressField.autocomplete_options, 'additionalQueryParams', {
          city_district_id: this.formDataCopy?.city_district?.id ?? this.formDataCopy?.city_district ?? null
        })

        if (this.map && this.googleMaps) {
          // map is already ready, run the handler immediately
          await this.drawCityDistrictPolygon(polygon)
          await this.fetchAddressStreets()
        }
        else {
          // map is not ready, queue handlers
          this.handlersAfterMapInitialization.push(() => this.drawCityDistrictPolygon(polygon))
          this.handlersAfterMapInitialization.push(async () => await this.fetchAddressStreets())
        }
      }

      if (value === '') {
        this.lastSelectedCityDistrict = null
        this.$delete(this.addressField.autocomplete_options, 'additionalQueryParams')
        await this.fetchAddressStreets()
      }
    },

    async onCityDistrictSelection (event) {
      const { selectedItem, usePickedId, resetAddressStreet } = event || {}

      if (selectedItem && selectedItem.id && (!this.lastSelectedCityDistrict || this.lastSelectedCityDistrict.id !== selectedItem.id)) {
        if (this.createMode || this.manuallyChangingLocation) {
          if (selectedItem && selectedItem.id) {
            if (resetAddressStreet !== false) {
              this.updateData({
                address_street: null
              })
            }
            this.lastSelectedStreet = null
            this.$set(this.addressField.autocomplete_options, 'additionalQueryParams', {
              city_district_id: selectedItem.id
            })
            await this.fetchAddressStreets(!!usePickedId)
          }
        }

        this.lastSelectedCityDistrict = selectedItem
        this.drawCityDistrictPolygon(selectedItem?.polygon ?? null)
      }
    },

    drawCityDistrictPolygon (polygon) {
      if (polygon && typeof polygon === 'string' && this.map && this.googleMaps) {
        // Transform selected city districts coordinates from string '45.8090173 15.9569315, 45.8090562 15.9569344, ....' to [{lat: 45.8090173, lng: 15.9569315}, {lat: 45.8090562, lng: 15.9569344}, ...]
        const polygonCoords = polygon.split(', ').map(coordinate => {
          const [lat, lng] = coordinate.split(' ').map(Number)
          return { lat, lng }
        })

        if (this.polygon) {
          this.polygon.setMap(null)
          this.polygon = null
        }
        // Create polygon
        this.polygon = new this.googleMaps.Polygon({
          paths: polygonCoords,
          strokeWeight: 0,
          fillColor: '#EF4444',
          fillOpacity: 0.20,
          clickable: false
        })

        // Add to map
        this.polygon.setMap(this.map)

        // Fit map bounds to the selected city district's polygon only when address street and number are not entered
        const bounds = new this.googleMaps.LatLngBounds()
        polygonCoords.forEach(coord => bounds.extend(coord))
        this.map.fitBounds(bounds)
      }
    },

    async onStreetInput (event) {
      const { value } = event || {}

      this.street = value ?? ''
      if (!this.map || !this.googleMaps || !this.AdvancedMarker) {
        this.handlersAfterMapInitialization.push(() => this.handleAddressStreetAndNumberChanges())
      }
      else {
        this.handleAddressStreetAndNumberChanges()
      }
    },

    onStreetSelection (event) {
      const { selectedItem } = event || {}
      if (selectedItem && selectedItem.id && (!this.lastSelectedStreet || this.lastSelectedStreet.id !== selectedItem.id)) {
        this.lastSelectedStreet = selectedItem

        // On create, when city district is not selected yet at this moment when user selected street
        if (!this.formDataCopy.city_district) {
          if (this.cityDistricts.items) {
            const foundCityDistrict = this.cityDistricts.items.find(item => item.id === (selectedItem?.city_district_id ?? null))
            if (foundCityDistrict) {
              this.updateData({
                city_district: foundCityDistrict
              })
              this.$set(this.formDataCopy, 'city_district', foundCityDistrict)
              this.onCityDistrictSelection({
                selectedItem: foundCityDistrict,
                usePickedId: true,
                resetAddressStreet: false
              })
            }
          }
        }
      }
    },

    onHouseNumberInput (value) {
      this.houseNumber = value ?? ''
      if (!this.map || !this.googleMaps || !this.AdvancedMarker) {
        this.handlersAfterMapInitialization.push(() => this.handleAddressStreetAndNumberChanges())
      }
      else {
        this.handleAddressStreetAndNumberChanges()
      }
    },

    handleAddressStreetAndNumberChanges: debounce(async function () {
      const that = this
      try {
        if (this.houseNumber && this.street) {
          that.showLoader = true
          let response
          let existingCoordinates = false

          // If we have coordinates already, do not georeverse
          if (this.formDataCopy.latitude && this.formDataCopy.longitude && !this.manuallyChangingLocation) {
            this.manuallyChangingLocation = true
            response = [
              {
                y: this.formData.latitude,
                x: this.formData.longitude
              }
            ]
            existingCoordinates = true
          }
          else {
            const match = this.street.match(/^(.+?)\s*\((.+?)\)$/)
            let search = ''
            if (match) {
              const streetName = match[1].trim()
              const neighborhood = match[2].trim()
              search = `${streetName} ${this.houseNumber}, ${neighborhood}, Zagreb, grad Zagreb`
            }
            else {
              // fallback if no parentheses are present
              search = `${this.street} ${this.houseNumber}, Zagreb, grad Zagreb`
            }

            response = await api('satellite-tracking').get('map-search', {
              mapType: 'here',
              q: search
            })
          }

          // here api returns array of matched coordinates, we get first one
          const { y: lat, x: lng } = response[0] || {}
          // if we get coords, just zoom map on them
          if (lat && lng) {
            const latLng = { lat, lng }
            // set marker on map if there is existing coordinates, otherwise zoom map on it but not display it
            if (existingCoordinates) {
              this.setMarkerAndUpdateCoordsInFormData(latLng)
            }

            // Center and zoom the map without adding a marker
            that.map.setCenter(latLng)
            that.map.setZoom(DEFAULT_ZOOM_LEVEL)
            that.showLoader = false
          }
          // if there is no coordinates, zoom on selected city district
          else {
            this.drawCityDistrictPolygon(this.lastSelectedCityDistrict)
            that.showLoader = false
          }
        }
        else {
          if (this.marker) {
            this.marker.setMap(null)
            this.marker = null
          }
        }
      }
      catch (exception) {
        that.showLoader = false
        console.error(exception)
      }
    }, GEOREVERSE_TIMEOUT_IN_MS),

    setMapClickAndDragendEvents () {
      const that = this
      if (this.mapClickListener) {
        this.mapClickListener.remove()
      }

      this.mapClickListener = this.map.addListener('click', (event) => {
        // stop showing POIs info popup
        if (event?.placeId) {
          event.stop()
        }

        that.setMarkerAndUpdateCoordsInFormData({
          lat: event?.latLng?.lat(),
          lng: event?.latLng?.lng()
        })
      })
    },

    setMarkerAndUpdateCoordsInFormData (latLng) {
      // If marker already exists, just move it
      const that = this
      if (this.marker) {
        this.marker.position = latLng
      }
      else {
        // Create a new marker
        this.marker = new this.AdvancedMarker({
          map: this.map,
          position: latLng,
          gmpDraggable: this.edit || this.createMode
        })

        // set dragend event on the marker to update coordinates when user moves marker on map
        this.marker.addListener('dragend', function (event) {
          that.updateData({
            latitude: event?.latLng?.lat(),
            longitude: event?.latLng?.lng()
          })
        })
      }

      // update form data lat and lng
      this.updateData({
        latitude: latLng?.lat,
        longitude: latLng?.lng
      })
    }
  }
}
</script>

<style scoped>
.title {
  font-family: 'Satoshi', sans-serif !important;
  font-size: 20px !important;
  font-weight: 500;
  color: #09090B;
  line-height: 28px;
  display: flex;
  justify-content: start;
  padding-top: 0 !important;
  padding-left: 0;
  padding-bottom: 10px;
}

.v-card-text {
  border: 1px solid #E5E7EB;
  border-radius: 16px !important;
  padding: 24px;
}

.v-card {
  margin: 0 16px 0 16px;
}
</style>
